import React from "react";
import { Icon } from "./siIcons";
import { siApi } from "./siApi";
import { aUnCompte, fmtD, joursAvant } from "./siModel";
import SiSauvegardesStats, { SqueletteSauvegardes, TableSquelette } from "./SiSauvegardesStats";
import { BoutonAnnotations, PanneauAnnotations, useAnnotations } from "./SiAnnotations";

// Tableaux de l'onglet Système dont les lignes portent notes et commentaires
// (mêmes clés que TABLES_ANNOTABLES côté backend).
const TABLES_ANNOTABLES = { vm: "Machine virtuelle" };

// Onglet « Système » de l'écran S.I — maquette claude-design/si-system.jsx,
// branchée sur l'API. Sections libres : fiches clé / valeur (« kv ») et
// tableaux (« table »), plus deux sections calculées (« cabinet » et
// « apps »). Les secrets ne sont jamais renvoyés dans la liste : « Afficher »
// et « Copier » les demandent au serveur, qui journalise la consultation.

const TYPES_KV = [["", "Texte"], ["url", "Lien"], ["mail", "Mail"], ["date", "Date"], ["mono", "Code / identifiant"], ["secret", "Secret"]];
const TYPES_TABLE = [["", "Texte"], ["num", "Nombre"], ["date", "Date"], ["expiry", "Échéance (alerte 30 j)"], ["url", "Lien"], ["mono", "Code / identifiant"], ["state", "État"]];
const uid = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;

function parentDefilant(el) {
  let p = el && el.parentElement;
  while (p) {
    const o = getComputedStyle(p).overflowY;
    if ((o === "auto" || o === "scroll") && p.scrollHeight > p.clientHeight) return p;
    p = p.parentElement;
  }
  return document.scrollingElement;
}

/* ---------- Ordre des champs et colonnes ---------- */

const deplacerDans = (liste, de, vers) => {
  const l = [...liste];
  const [x] = l.splice(de, 1);
  l.splice(vers, 0, x);
  return l;
};

// Glisser-déposer d'une liste : on saisit l'élément par sa poignée, la liste
// se réordonne au survol. Lâché hors de la liste (ou Échap) : l'ordre de
// départ revient. `terminer(nouvelle, ancienne)`, s'il est fourni, enregistre
// l'ordre au dépôt (sections, lignes, applications) ; sans lui, l'ordre
// attend « Enregistrer » (champs d'une fiche, colonnes d'un tableau). Au
// clavier : flèches haut / bas sur la poignée.
// Branchement : `zone` sur le conteneur, `cible(i)` sur chaque élément,
// `poignee(i)` sur sa poignée.
function useGlisser(liste, changer, { terminer } = {}) {
  const courante = React.useRef(liste);
  courante.current = liste;
  const enCours = React.useRef(null);
  const depart = React.useRef(null);
  const [glisse, setGlisse] = React.useState(null);
  const suivre = (i) => {
    enCours.current = i;
    setGlisse(i);
  };
  const deplacer = (de, vers) => {
    const l = deplacerDans(courante.current, de, vers);
    courante.current = l;
    changer(l);
  };
  const fin = (avant) => {
    const apres = courante.current;
    if (terminer && apres.some((x, k) => x !== avant[k])) terminer(apres, avant);
  };
  // Dépôt dans la liste : ordre gardé. Fin sans dépôt : ordre de départ.
  const arreter = (garder) => {
    const avant = depart.current;
    depart.current = null;
    suivre(null);
    if (!avant) return;
    if (garder) return fin(avant);
    courante.current = avant;
    changer(avant);
  };
  const poignee = (i) => ({
    draggable: true,
    onDragStart: (e) => {
      e.dataTransfer.effectAllowed = "move";
      // Type propre : les champs de saisie n'acceptent pas ce dépôt.
      e.dataTransfer.setData("application/x-si-ordre", String(i));
      const el = e.currentTarget.closest("[data-glisser]");
      if (el) e.dataTransfer.setDragImage(el, 12, el.offsetHeight / 2);
      depart.current = courante.current;
      suivre(i);
    },
    // Après un dépôt, déjà traité par la zone (départ effacé) : sans effet.
    onDragEnd: () => arreter(false),
    onKeyDown: (e) => {
      const j = e.key === "ArrowUp" ? i - 1 : e.key === "ArrowDown" ? i + 1 : null;
      if (j === null || j < 0 || j >= courante.current.length) return;
      e.preventDefault();
      const avant = courante.current;
      deplacer(i, j);
      fin(avant);
    },
  });
  const cible = (i) => ({
    "data-glisser": "",
    onDragOver: (e) => {
      const de = enCours.current;
      if (de === null || de === i) return;
      // On ne déplace qu'une fois la moitié de l'élément survolé franchie,
      // sinon deux éléments de hauteurs différentes s'échangeraient en boucle.
      const r = e.currentTarget.getBoundingClientRect();
      const avant = e.clientY < r.top + r.height / 2;
      if ((de < i && avant) || (de > i && !avant)) return;
      deplacer(de, i);
      suivre(i);
    },
  });
  const zone = {
    onDragOver: (e) => {
      if (enCours.current !== null) e.preventDefault();
    },
    onDrop: (e) => {
      if (enCours.current === null) return;
      e.preventDefault();
      arreter(true);
    },
  };
  return { glisse, poignee, cible, zone };
}

// Remet une liste dans l'ordre de `cles` (annulation après un refus du
// serveur), en gardant le contenu à jour de chaque élément.
const selonOrdre = (liste, cles, cleDe = (x) => x.cle) => {
  const rang = new Map(cles.map((c, k) => [c, k]));
  return [...liste].sort((a, b) => (rang.get(cleDe(a)) ?? Infinity) - (rang.get(cleDe(b)) ?? Infinity));
};

const Poignee = (props) => (
  <span className="sys-grip" role="button" tabIndex={0} title="Glisser pour déplacer" aria-label="Déplacer (flèches haut et bas)" {...props}>
    <Icon name="grip" size={14} />
  </span>
);

/* ---------- Valeurs ---------- */

const ValeurSecrete = ({ sec, cle, notifier }) => {
  const [valeur, setValeur] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const lire = async () => {
    setBusy(true);
    try {
      const r = await siApi.lireSecret(sec.cle, cle);
      return r.valeur;
    } catch (e) {
      notifier(e.message, { type: "erreur" });
      return null;
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="sys-secret">
      <span className="si-mono">{valeur ?? "•".repeat(18)}</span>
      <button className="sys-mini" disabled={busy} onClick={async () => (valeur !== null ? setValeur(null) : setValeur(await lire()))}>{valeur !== null ? "Masquer" : "Afficher"}</button>
      <button
        className="sys-mini"
        disabled={busy}
        onClick={async () => {
          const v = valeur ?? (await lire());
          if (v === null) return;
          try {
            await navigator.clipboard.writeText(v);
            notifier("Copié dans le presse-papiers");
          } catch (e) {
            notifier("Copie impossible depuis ce navigateur", { type: "erreur" });
          }
        }}
      >
        Copier
      </button>
    </span>
  );
};

const Valeur = ({ type, v, secretDefini, sec, cle, notifier }) => {
  if (type === "secret") return secretDefini ? <ValeurSecrete sec={sec} cle={cle} notifier={notifier} /> : <span className="mdt-muted">Non renseigné</span>;
  if (!v) return <span className="mdt-muted">Non renseigné</span>;
  if (type === "url") return <a className="sys-a" href={v} target="_blank" rel="noopener noreferrer">{v.replace(/^https?:\/\//, "")}<Icon name="open" size={11} /></a>;
  if (type === "mail") return <a className="sys-a" href={`mailto:${v}`}>{v}</a>;
  if (type === "date") return <span>{fmtD(v)}</span>;
  if (type === "mono") return <span className="si-mono">{v}</span>;
  return <span>{v}</span>;
};

const Cellule = ({ type, v }) => {
  if (type === "state") return <span className={`si-st ${/décom|arrêt/i.test(v || "") ? "is-out" : "is-on"}`}>{v || "—"}</span>;
  if (type === "expiry") {
    const d = joursAvant(v);
    if (d == null) return <span className="mdt-muted">—</span>;
    const cls = d < 0 ? "is-out" : d <= 30 ? "is-in" : "is-on";
    return <span className={`si-st ${cls}`}>{fmtD(v)}{d < 0 ? " · expiré" : d <= 30 ? ` · ${d} j` : ""}</span>;
  }
  if (type === "date") return v ? <span>{fmtD(v)}</span> : <span className="mdt-muted">—</span>;
  if (type === "url") return v ? <a className="sys-a" href={v} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Ouvrir<Icon name="open" size={11} /></a> : <span className="mdt-muted">—</span>;
  if (!v) return <span className="mdt-muted">—</span>;
  return <span className={type === "mono" ? "si-mono" : ""}>{v}</span>;
};

/* ---------- Cartes ---------- */

const Carte = ({ sec, children, action, editing, title, setTitle, onDelete }) => {
  const [ask, setAsk] = React.useState(false);
  return (
    <section className={`sys-card ${editing ? "is-editing" : ""}`} id={`sys-${sec.cle}`} data-sys={sec.cle}>
      <div className="sys-card-h">
        <span className="mf-ico"><Icon name={sec.icone || "fileText"} size={14} /></span>
        {editing ? <input className="mf-input si-in sys-title-in" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre de la section" aria-label="Titre de la section" /> : <h3>{sec.libelle}</h3>}
        <span style={{ flex: 1 }}></span>
        {ask ? (
          <span className="sys-ask">
            Supprimer cette section et son contenu ?
            <button className="btn sys-btn-del" onClick={onDelete}>Supprimer</button>
            <button className="btn" onClick={() => setAsk(false)}>Annuler</button>
          </span>
        ) : (
          <>
            {action}
            {!editing && onDelete && <button className="mdt-ic del" title="Supprimer la section" aria-label="Supprimer la section" onClick={() => setAsk(true)}><Icon name="trash" size={13} /></button>}
          </>
        )}
      </div>
      {children}
    </section>
  );
};

const BarreEdition = ({ onCancel, onSave, busy }) => (
  <div className="sys-act">
    <button className="btn" onClick={onCancel} disabled={busy}>Annuler</button>
    <button className="btn primary" onClick={onSave} disabled={busy}>{busy ? "Enregistrement…" : "Enregistrer"}</button>
  </div>
);

// Fiche clé / valeur. Secret : vide = inchangé, « Effacer » = supprimé.
const CarteKv = ({ sec, enregistrer, onDelete, startEdit, notifier }) => {
  const init = () => ({ titre: sec.libelle, champs: sec.champs.map((c) => ({ ...c })), v: { ...(sec.donnees || {}) }, effaces: {} });
  const [ed, setEd] = React.useState(startEdit ? init : null);
  const [busy, setBusy] = React.useState(false);
  const setC = (i, p) => setEd((e) => ({ ...e, champs: e.champs.map((c, j) => (j === i ? { ...c, ...p } : c)) }));
  const g = useGlisser(ed ? ed.champs : [], (l) => setEd((e) => ({ ...e, champs: l })));
  const secrets = new Set(sec.secretsDefinis || []);
  const sauver = async () => {
    setBusy(true);
    const donnees = { ...ed.v };
    Object.keys(ed.effaces).forEach((k) => { donnees[k] = null; });
    const ok = await enregistrer({ libelle: ed.titre.trim() || sec.libelle, champs: ed.champs, donnees });
    setBusy(false);
    if (ok) setEd(null);
  };
  return (
    <Carte
      sec={sec}
      editing={!!ed}
      title={ed && ed.titre}
      setTitle={(t) => setEd({ ...ed, titre: t })}
      onDelete={onDelete}
      action={ed ? <BarreEdition busy={busy} onCancel={() => setEd(null)} onSave={sauver} /> : <button className="mdt-ic eye" title="Modifier" aria-label="Modifier la section" onClick={() => setEd(init())}><Icon name="pencil" size={13} /></button>}
    >
      {ed ? (
        <div className="sys-kv-ed" {...g.zone}>
          {ed.champs.map((c, i) => {
            const secretDefini = c.type === "secret" && secrets.has(c.cle) && !ed.effaces[c.cle];
            return (
              <div key={c.cle} className={`sys-kv-row ${g.glisse === i ? "is-glisse" : ""}`} {...g.cible(i)}>
                <Poignee {...g.poignee(i)} />
                <input className="mf-input si-in sys-lbl-in" value={c.libelle} onChange={(e) => setC(i, { libelle: e.target.value })} placeholder="Libellé" aria-label="Libellé du champ" />
                <span className="six-kv-val">
                  <input
                    type={c.type === "date" ? "date" : c.type === "secret" ? "password" : "text"}
                    autoComplete="off"
                    className={`mf-input si-in ${c.type === "mono" || c.type === "secret" ? "si-mono" : ""}`}
                    value={ed.v[c.cle] || ""}
                    onChange={(e) => {
                      // Une nouvelle saisie annule un « Effacer » précédent.
                      const { [c.cle]: _efface, ...effaces } = ed.effaces;
                      setEd({ ...ed, effaces, v: { ...ed.v, [c.cle]: e.target.value } });
                    }}
                    placeholder={secretDefini ? "Inchangé (saisir pour remplacer)" : "Valeur"}
                    aria-label={c.libelle || "Valeur"}
                  />
                  {secretDefini && <button type="button" className="sys-mini" onClick={() => setEd({ ...ed, effaces: { ...ed.effaces, [c.cle]: true }, v: { ...ed.v, [c.cle]: "" } })}>Effacer</button>}
                </span>
                <select className="mf-input si-in sys-type" value={c.type || ""} onChange={(e) => setC(i, { type: e.target.value })} aria-label="Type du champ">{TYPES_KV.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                <button className="mdt-ic del" title="Retirer ce champ" aria-label="Retirer ce champ" onClick={() => setEd({ ...ed, champs: ed.champs.filter((_, j) => j !== i) })}><Icon name="x" size={13} /></button>
              </div>
            );
          })}
          <button className="mf-link sys-addf" onClick={() => setEd({ ...ed, champs: [...ed.champs, { cle: uid(), libelle: "", type: "" }] })}><Icon name="plus" size={12} /> Ajouter un champ</button>
        </div>
      ) : (
        <div className="sys-kv">
          {sec.champs.map((c) => (
            <React.Fragment key={c.cle}>
              <span>{c.libelle || "—"}</span>
              <b><Valeur type={c.type} v={(sec.donnees || {})[c.cle]} secretDefini={secrets.has(c.cle)} sec={sec} cle={c.cle} notifier={notifier} /></b>
            </React.Fragment>
          ))}
          {sec.champs.length === 0 && <span className="mdt-muted">Aucun champ. Cliquez sur le crayon pour en ajouter.</span>}
        </div>
      )}
    </Carte>
  );
};

const CarteTable = ({ sec, enregistrer, onDelete, intro, startEdit, notifier }) => {
  const base = Array.isArray(sec.donnees) ? sec.donnees : [];
  // Ordre des lignes pendant un glisser-déposer, avant son enregistrement.
  const [ordreLignes, setOrdreLignes] = React.useState(null);
  const rows = ordreLignes || base;
  const [ed, setEd] = React.useState(null);
  // Notes et commentaires par ligne (tableaux de TABLES_ANNOTABLES : VM).
  const typeLigne = TABLES_ANNOTABLES[sec.cle];
  const [annotations, majAnnotation] = useAnnotations(typeLigne ? sec.cle : null, notifier);
  const [ouverte, setOuverte] = React.useState(null);
  const fermerAnnotations = React.useCallback(() => setOuverte(null), []);
  const titreLigne = (r) => r[sec.champs[0]?.cle] || "Ligne sans nom";
  const initS = () => ({ titre: sec.libelle, champs: sec.champs.map((c) => ({ ...c })) });
  const [st, setSt] = React.useState(startEdit ? initS : null);
  const [busy, setBusy] = React.useState(false);
  const setC = (i, p) => setSt((s) => ({ ...s, champs: s.champs.map((c, j) => (j === i ? { ...c, ...p } : c)) }));
  const g = useGlisser(st ? st.champs : [], (l) => setSt((s) => ({ ...s, champs: l })));
  // Lignes : l'ordre est enregistré au dépôt (identifiant `_id` de chaque
  // ligne conservé, donc notes et commentaires des VM aussi).
  const gl = useGlisser(rows, (l) => setOrdreLignes(l === base ? null : l), {
    terminer: (l) => {
      setOrdreLignes(null);
      enregistrer({ donnees: l }, "Ordre des lignes enregistré");
    },
  });
  const vide = () => Object.fromEntries(sec.champs.map((c) => [c.cle, ""]));
  const sauverLignes = async (donnees) => {
    setBusy(true);
    const ok = await enregistrer({ donnees });
    setBusy(false);
    if (ok) setEd(null);
  };
  const sauverStructure = async () => {
    setBusy(true);
    const ok = await enregistrer({ libelle: st.titre.trim() || sec.libelle, champs: st.champs });
    setBusy(false);
    if (ok) setSt(null);
  };
  const list = ed && ed.i === -1 ? [...rows, ed.v] : rows;
  const ordonnable = !ed && rows.length > 1;
  return (
    <Carte
      sec={sec}
      editing={!!st}
      title={st && st.titre}
      setTitle={(t) => setSt({ ...st, titre: t })}
      onDelete={onDelete}
      action={
        st ? (
          <BarreEdition busy={busy} onCancel={() => setSt(null)} onSave={sauverStructure} />
        ) : (
          !ed && (
            <>
              <button className="btn sys-add" onClick={() => setEd({ i: -1, v: vide() })}><Icon name="plus" size={13} /> Ajouter une ligne</button>
              <button className="mdt-ic eye" title="Modifier le titre et les colonnes" aria-label="Modifier le titre et les colonnes" onClick={() => setSt(initS())}><Icon name="settings" size={13} /></button>
            </>
          )
        )
      }
    >
      {st && (
        <div className="sys-cols-ed" {...g.zone}>
          <span className="sys-cols-l">Colonnes</span>
          {st.champs.map((c, i) => (
            <div key={c.cle} className={`sys-col-chip ${g.glisse === i ? "is-glisse" : ""}`} {...g.cible(i)}>
              <Poignee {...g.poignee(i)} />
              <input className="mf-input si-in" value={c.libelle} onChange={(e) => setC(i, { libelle: e.target.value })} placeholder="Nom de colonne" aria-label="Nom de colonne" />
              <select className="mf-input si-in sys-type" value={c.type || ""} onChange={(e) => setC(i, { type: e.target.value })} aria-label="Type de colonne">{TYPES_TABLE.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              <button className="mdt-ic del" title="Retirer la colonne" aria-label="Retirer la colonne" onClick={() => setSt({ ...st, champs: st.champs.filter((_, j) => j !== i) })}><Icon name="x" size={13} /></button>
            </div>
          ))}
          <button className="mf-link sys-addf" onClick={() => setSt({ ...st, champs: [...st.champs, { cle: uid(), libelle: "", type: "" }] })}><Icon name="plus" size={12} /> Ajouter une colonne</button>
        </div>
      )}
      {ouverte && (
        <PanneauAnnotations cible={sec.cle} refId={ouverte._id} type={typeLigne} titre={titreLigne(ouverte)} onClose={fermerAnnotations} onChange={majAnnotation} notifier={notifier} />
      )}
      {!st && intro}
      {!st && (
        <div className="mdt-wrap sys-tw">
          <table className="mdt-t sys-t">
            <thead><tr><th className="sys-grip-c"></th>{sec.champs.map((c) => <th key={c.cle} className={c.type === "num" ? "r" : ""}>{c.libelle}</th>)}<th></th></tr></thead>
            <tbody {...gl.zone}>
              {list.map((r, i) => {
                const editing = ed && (ed.i === i || (ed.i === -1 && i === rows.length));
                return (
                  <tr key={r._id || `n${i}`} className={`${editing ? "si-row is-editing" : ""} ${gl.glisse === i ? "is-glisse" : ""}`} {...(ordonnable ? gl.cible(i) : {})}>
                    <td className="sys-grip-c">{ordonnable && <Poignee {...gl.poignee(i)} />}</td>
                    {sec.champs.map((c) => (
                      <td key={c.cle} className={c.type === "num" ? "r" : ""}>
                        {editing ? (
                          <input
                            type={c.type === "date" || c.type === "expiry" ? "date" : "text"}
                            className={`mf-input si-in ${c.type === "num" ? "sys-in-n" : ""}`}
                            value={ed.v[c.cle] || ""}
                            onChange={(e) => setEd({ ...ed, v: { ...ed.v, [c.cle]: e.target.value } })}
                            aria-label={c.libelle}
                          />
                        ) : (
                          <Cellule type={c.type} v={r[c.cle]} />
                        )}
                      </td>
                    ))}
                    <td className="lm-act">
                      {editing ? (
                        <>
                          <button className="tk-close-btn" disabled={busy} onClick={() => sauverLignes(ed.i === -1 ? [...rows, ed.v] : rows.map((x, j) => (j === ed.i ? ed.v : x)))}><Icon name="check" size={13} /> Enregistrer</button>
                          <button className="mdt-ic" title="Annuler" aria-label="Annuler" onClick={() => setEd(null)}><Icon name="x" size={13} /></button>
                        </>
                      ) : (
                        !ed && (
                          <>
                            {typeLigne && r._id && (
                              <BoutonAnnotations resume={annotations[r._id]} libelle={titreLigne(r)} onClick={() => setOuverte(r)} />
                            )}
                            <button className="mdt-ic eye" title="Modifier la ligne" aria-label="Modifier la ligne" onClick={() => setEd({ i, v: { ...r } })}><Icon name="pencil" size={13} /></button>
                            <button className="mdt-ic del" title="Supprimer la ligne" aria-label="Supprimer la ligne" onClick={() => enregistrer({ donnees: rows.filter((_, j) => j !== i) }, "Ligne supprimée")}><Icon name="trash" size={13} /></button>
                          </>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
              {list.length === 0 && <tr><td colSpan={sec.champs.length + 2} className="mdt-empty">Aucune ligne</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </Carte>
  );
};

const CarteTitre = ({ sec, renommer, onDelete, extra, children }) => {
  const [t, setT] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  return (
    <Carte
      sec={sec}
      editing={t != null}
      title={t}
      setTitle={setT}
      onDelete={onDelete}
      action={
        t != null ? (
          <BarreEdition
            busy={busy}
            onCancel={() => setT(null)}
            onSave={async () => {
              setBusy(true);
              const ok = await renommer(t.trim() || sec.libelle);
              setBusy(false);
              if (ok) setT(null);
            }}
          />
        ) : (
          <>{extra}<button className="mdt-ic eye" title="Renommer" aria-label="Renommer la section" onClick={() => setT(sec.libelle)}><Icon name="pencil" size={13} /></button></>
        )
      }
    >
      {children}
    </Carte>
  );
};

// Référentiel des applications : comptes calculés depuis l'onglet
// Utilisateurs (sorties comprises, à révoquer), le reste est saisi ici.
const TableApplications = ({ apps, setApps, lignes, sites, majApplication, notifier }) => {
  const [ed, setEd] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [annotations, majAnnotation] = useAnnotations("application", notifier);
  const [ouverte, setOuverte] = React.useState(null);
  const fermer = React.useCallback(() => setOuverte(null), []);
  // Ordre enregistré au dépôt ; c'est aussi celui des colonnes d'accès de
  // l'onglet Utilisateurs.
  const g = useGlisser(apps, setApps, {
    terminer: async (l, avant) => {
      try {
        await siApi.ordonnerApplications(l.map((a) => a.cle));
        notifier("Ordre des applications enregistré");
      } catch (e) {
        setApps((xs) => selonOrdre(xs, avant.map((a) => a.cle)));
        notifier(e.message, { type: "erreur" });
      }
    },
  });
  const ordonnable = !ed && apps.length > 1;
  return (
    <div className="mdt-wrap sys-tw">
      {ouverte && (
        <PanneauAnnotations cible="application" refId={ouverte.cle} type="Application" titre={ouverte.libelle} onClose={fermer} onChange={majAnnotation} notifier={notifier} />
      )}
      <table className="mdt-t sys-t">
        <thead><tr><th className="sys-grip-c"></th><th>Application</th><th>Usage</th><th>Éditeur</th><th>Hébergement</th><th className="r">Comptes</th>{sites.map((x) => <th key={x} className="r">{x}</th>)}<th className="r">Licences</th><th>Renouvellement</th><th>Support</th><th></th></tr></thead>
        <tbody {...g.zone}>
          {apps.map((a, i) => {
            const e = ed && ed.cle === a.cle ? ed : null;
            const users = lignes.filter((r) => aUnCompte(r, a));
            const over = a.licences != null && users.length > a.licences;
            const rev = lignes.filter((r) => r.statut === "sortie" && aUnCompte(r, a)).length;
            const fld = (k, cls, type) => <td><input type={type || "text"} className={`mf-input si-in ${cls || ""}`} value={e[k] ?? ""} onChange={(ev) => setEd({ ...e, [k]: ev.target.value })} aria-label={k} /></td>;
            return (
              <tr key={a.cle} className={`${e ? "si-row is-editing" : ""} ${g.glisse === i ? "is-glisse" : ""}`} {...(ordonnable ? g.cible(i) : {})}>
                <td className="sys-grip-c">{ordonnable && <Poignee {...g.poignee(i)} />}</td>
                <td className="sys-app">{a.libelle}</td>
                {e ? fld("usage") : <td className="mdt-dim">{a.usage || "—"}</td>}
                {e ? fld("editeur") : <td>{a.editeur || "—"}</td>}
                {e ? fld("hebergement") : <td className="mdt-dim">{a.hebergement || "—"}</td>}
                <td className="r"><b className="sys-n" title={users.map((r) => r.nomComplet).join("\n")}>{users.length}</b>{rev > 0 && <small className="sys-rev" title="Comptes à révoquer (sorties en cours)">−{rev}</small>}</td>
                {sites.map((x) => <td key={x} className="r mdt-dim">{users.filter((r) => r.site === x).length || "·"}</td>)}
                {e ? fld("licences", "sys-in-n") : <td className="r">{a.licences != null ? <span className={over ? "sys-over" : ""} title={over ? "Plus de comptes que de licences" : ""}>{a.licences}</span> : <span className="mdt-muted">—</span>}</td>}
                {e ? fld("renouvellement", "", "date") : <td className="mdt-dim">{a.renouvellement ? fmtD(a.renouvellement) : "—"}</td>}
                {e ? fld("support") : <td>{a.support || "—"}</td>}
                <td className="lm-act">
                  {e ? (
                    <>
                      <button
                        className="tk-close-btn"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          const ok = await majApplication(a, { usage: e.usage, editeur: e.editeur, hebergement: e.hebergement, licences: e.licences === "" ? null : e.licences, renouvellement: e.renouvellement || null, support: e.support });
                          setBusy(false);
                          if (ok) setEd(null);
                        }}
                      >
                        <Icon name="check" size={13} /> Enregistrer
                      </button>
                      <button className="mdt-ic" title="Annuler" aria-label="Annuler" onClick={() => setEd(null)}><Icon name="x" size={13} /></button>
                    </>
                  ) : (
                    !ed && (
                      <>
                        <BoutonAnnotations resume={annotations[a.cle]} libelle={a.libelle} onClick={() => setOuverte(a)} />
                        <button className="mdt-ic eye" title="Modifier la ligne" aria-label={`Modifier ${a.libelle}`} onClick={() => setEd({ ...a, licences: a.licences ?? "" })}><Icon name="pencil" size={13} /></button>
                      </>
                    )
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const CarteSquelette = () => (
  <section className="sys-card">
    <div className="sys-card-h"><span className="six-sk" style={{ width: 160, height: 14 }} /></div>
    <div className="sys-kv">{[0, 1, 2].map((i) => <React.Fragment key={i}><span className="six-sk" style={{ width: 120 }} /><span className="six-sk" style={{ width: 220 }} /></React.Fragment>)}</div>
  </section>
);

// Onglet Sauvegardes : réplique de la carte (en-tête, relevé, tableau).
const CarteSauvegardesSquelette = () => (
  <section className="sys-card" aria-busy="true" aria-label="Chargement des sauvegardes">
    <div className="sys-card-h" aria-hidden="true">
      <span className="mf-ico"><Icon name="archive" size={14} /></span>
      <span className="six-sk" style={{ width: 110, height: 14 }} />
      <span style={{ flex: 1 }}></span>
      <span className="six-sk" style={{ width: 128, height: 28, borderRadius: 6 }} />
      <span className="six-sk" style={{ width: 28, height: 28, borderRadius: 6 }} />
      <span className="six-sk" style={{ width: 28, height: 28, borderRadius: 6 }} />
    </div>
    <SqueletteSauvegardes />
    <TableSquelette colonnes={7} lignes={3} />
  </section>
);

/* ---------- Onglet ---------- */

// La section Sauvegardes a son propre onglet (demande de Jonathan du
// 20/10/2026) : vue="sauvegardes" ne montre qu'elle, sans menu des sections ;
// l'onglet Système (vue par défaut) l'exclut.
const SECTION_SAUVEGARDES = "backup";

export default function SiSysteme({ lignes, apps, setApps, sites, onUsers, notifier, vue = "systeme" }) {
  const [secs, setSecs] = React.useState(null);
  const [active, setActive] = React.useState("");
  const [fresh, setFresh] = React.useState(null);
  const rootRef = React.useRef(null);
  const sauvegardes = vue === "sauvegardes";
  // Onglet Sauvegardes : relevé chargé en même temps que la section, pour
  // que tableau et statistiques s'affichent d'un coup ({ donnees } | { erreur }).
  const [releve, setReleve] = React.useState(null);

  React.useEffect(() => {
    let actif = true;
    const releveCharge = sauvegardes
      ? siApi.sauvegardes().then((donnees) => ({ donnees }), (e) => ({ erreur: e.message }))
      : null;
    Promise.all([siApi.sections(), releveCharge])
      .then(([toutes, r]) => {
        if (!actif) return;
        const s = toutes.filter((x) => (x.cle === SECTION_SAUVEGARDES) === sauvegardes);
        setReleve(r);
        setSecs(s);
        setActive(s[0]?.cle || "");
      })
      .catch((e) => {
        if (!actif) return;
        setSecs([]);
        notifier(e.message, { type: "erreur" });
      });
    return () => {
      actif = false;
    };
  }, [notifier, sauvegardes]);

  // Menu des sections : suit le défilement.
  React.useEffect(() => {
    if (!secs) return undefined;
    const sp = parentDefilant(rootRef.current);
    const tgt = sp === document.scrollingElement ? window : sp;
    const onScroll = () => {
      const top = sp === document.scrollingElement ? 0 : sp.getBoundingClientRect().top;
      let cur = null;
      rootRef.current?.querySelectorAll("[data-sys]").forEach((el) => {
        if (!cur || el.getBoundingClientRect().top - top < 120) cur = el.dataset.sys;
      });
      if (cur) setActive(cur);
    };
    tgt.addEventListener("scroll", onScroll, { passive: true });
    return () => tgt.removeEventListener("scroll", onScroll);
  }, [secs]);

  const aller = React.useCallback((cle) => {
    const el = rootRef.current?.querySelector(`#sys-${CSS.escape(cle)}`);
    if (!el) return;
    const sp = parentDefilant(el);
    const top = sp === document.scrollingElement ? 0 : sp.getBoundingClientRect().top;
    sp.scrollTo({ top: sp.scrollTop + el.getBoundingClientRect().top - top - 12, behavior: "smooth" });
    setActive(cle);
  }, []);
  React.useEffect(() => {
    if (!fresh) return undefined;
    const h = setTimeout(() => aller(fresh), 50);
    return () => clearTimeout(h);
  }, [fresh, aller]);

  const remplacerSec = (s) => setSecs((xs) => xs.map((x) => (x.cle === s.cle ? s : x)));

  // Ordre des sections, par glisser-déposer dans le menu, enregistré au dépôt.
  const gs = useGlisser(secs || [], setSecs, {
    terminer: async (l, avant) => {
      try {
        const v = await siApi.ordonnerSections(l.map((s) => s.cle));
        const parCle = new Map(v.map((x) => [x.cle, x]));
        // Nouvelle version reprise, sauf si la section a été enregistrée
        // entre-temps (sa version est alors plus récente).
        setSecs((xs) => xs.map((s) => (parCle.has(s.cle) && parCle.get(s.cle).version > s.version ? { ...s, ...parCle.get(s.cle) } : s)));
        notifier("Ordre des sections enregistré");
      } catch (e) {
        setSecs((xs) => selonOrdre(xs, avant.map((s) => s.cle)));
        notifier(e.message, { type: "erreur" });
      }
    },
  });

  // Enregistre une section (affichée tout de suite, annulée si refusée ;
  // conflit : la version à jour du serveur est reprise).
  const enregistrer = async (sec, corps, message = "Section enregistrée") => {
    const avant = sec;
    if (corps.donnees && Array.isArray(corps.donnees)) remplacerSec({ ...sec, donnees: corps.donnees });
    try {
      const s = await siApi.modifierSection(sec.cle, { ...corps, version: sec.version });
      remplacerSec(s);
      notifier(message);
      return true;
    } catch (e) {
      if (e.status === 409 && e.data?.section) remplacerSec(e.data.section);
      else remplacerSec(avant);
      notifier(e.message, { type: "erreur" });
      return false;
    }
  };

  const supprimer = async (sec) => {
    setSecs((xs) => xs.filter((x) => x.cle !== sec.cle));
    try {
      await siApi.supprimerSection(sec.cle);
      notifier(`Section « ${sec.libelle} » supprimée`, {
        undo: async () => {
          try {
            const s = await siApi.restaurerSection(sec.cle);
            setSecs((xs) => [...xs, s].sort((a, b) => a.ordre - b.ordre));
            notifier("Section restaurée");
          } catch (e) {
            notifier(e.message, { type: "erreur" });
          }
        },
      });
    } catch (e) {
      setSecs((xs) => [...xs, sec].sort((a, b) => a.ordre - b.ordre));
      notifier(e.message, { type: "erreur" });
    }
  };

  const ajouter = async (type) => {
    try {
      const s = await siApi.creerSection(type);
      setSecs((xs) => [...xs, s]);
      setFresh(s.cle);
    } catch (e) {
      notifier(e.message, { type: "erreur" });
    }
  };

  const majApplication = async (a, corps) => {
    try {
      const maj = await siApi.modifierApplication(a.cle, corps);
      setApps((xs) => xs.map((x) => (x.cle === a.cle ? { ...x, ...maj } : x)));
      notifier("Application enregistrée");
      return true;
    } catch (e) {
      notifier(e.message, { type: "erreur" });
      return false;
    }
  };

  if (!secs) {
    return (
      <div className={sauvegardes ? "sys sys-seul" : "sys"} ref={rootRef}>
        {!sauvegardes && <nav className="sys-nav"><div className="sys-nav-h">Sections</div>{[0, 1, 2, 3, 4].map((i) => <span key={i} className="six-sk" style={{ width: "80%", margin: "8px 10px" }} />)}</nav>}
        <div className="sys-main">{sauvegardes ? <CarteSauvegardesSquelette /> : <><CarteSquelette /><CarteSquelette /></>}</div>
      </div>
    );
  }

  const parCle = Object.fromEntries(secs.map((s) => [s.cle, s]));
  const ext = lignes.filter((r) => r.type === "externe").length;
  const nIn = lignes.filter((r) => r.statut === "arrivee").length;
  const nOut = lignes.filter((r) => r.statut === "sortie").length;
  const vm = Array.isArray(parCle.vm?.donnees) ? parCle.vm.donnees : [];
  const hebergeur = parCle.hebergeur?.donnees?.nom;
  const sslSoon = parCle.ssl?.alertes || 0;
  const o365 = lignes.filter((r) => (r.acces || []).includes("o365")).length;

  return (
    <div className={sauvegardes ? "sys sys-seul" : "sys"} ref={rootRef}>
      {!sauvegardes && (
      <nav className="sys-nav" aria-label="Sections" {...gs.zone}>
        <div className="sys-nav-h">Sections</div>
        {secs.map((s, i) => (
          <div key={s.cle} className={`sys-nav-it ${gs.glisse === i ? "is-glisse" : ""}`} {...gs.cible(i)}>
            {secs.length > 1 && <Poignee {...gs.poignee(i)} />}
            <button className={active === s.cle ? "is-on" : ""} onClick={() => aller(s.cle)}>
              <Icon name={s.icone || "fileText"} size={14} /><span>{s.libelle}</span>{s.alertes > 0 && <i className="sys-flag">{s.alertes}</i>}
            </button>
          </div>
        ))}
        <div className="sys-nav-add">
          <button onClick={() => ajouter("kv")}><Icon name="plus" size={13} /><span>Nouvelle fiche</span></button>
          <button onClick={() => ajouter("table")}><Icon name="plus" size={13} /><span>Nouveau tableau</span></button>
        </div>
        <div className="sys-nav-foot"><button className="mf-link" onClick={onUsers}>Utilisateurs et accès →</button></div>
      </nav>
      )}
      <div className="sys-main">
        {secs.map((sec) => {
          const del = () => supprimer(sec);
          const renommer = (libelle) => enregistrer(sec, { libelle }, "Section renommée");
          if (sec.type === "cabinet")
            return (
              <CarteTitre key={sec.cle} sec={sec} renommer={renommer} onDelete={del}>
                <div className="sys-stats">
                  <div><b>{sites.length}</b><span>Sites</span><small>{sites.join(" · ")}</small></div>
                  <div><b>{lignes.length}</b><span>Utilisateurs</span><small>{ext > 0 ? `dont ${ext} hors système` : "tous dans MyALFA"}</small></div>
                  <div><b>{nIn + nOut}</b><span>Mouvements en cours</span><small>{nIn} arrivée{nIn > 1 ? "s" : ""} · {nOut} sortie{nOut > 1 ? "s" : ""}</small></div>
                  {parCle.vm && <div><b>{vm.length}</b><span>Machines virtuelles</span><small>{hebergeur ? `chez ${hebergeur}` : ""}</small></div>}
                  {parCle.ssl && <div className={sslSoon ? "is-warn" : ""}><b>{sslSoon}</b><span>Certificat{sslSoon > 1 ? "s" : ""} à renouveler</span><small>sous 30 jours</small></div>}
                </div>
              </CarteTitre>
            );
          if (sec.type === "kv")
            return <CarteKv key={sec.cle} sec={sec} startEdit={fresh === sec.cle} onDelete={del} notifier={notifier} enregistrer={(corps) => enregistrer(sec, corps)} />;
          if (sec.type === "table") {
            let intro = null;
            // Section Sauvegardes : statistiques des rapports quotidiens « Sauvegardes Singapour ».
            if (sec.cle === SECTION_SAUVEGARDES) intro = <SiSauvegardesStats initial={releve} />;
            if (sec.intro === "m365") {
              const lignesM365 = Array.isArray(sec.donnees) ? sec.donnees : [];
              const achetees = lignesM365.reduce((n, l) => n + (parseInt(l.qte, 10) || 0), 0);
              const attribuees = lignesM365.reduce((n, l) => n + (parseInt(l.attr, 10) || 0), 0);
              intro = (
                <div className="sys-intro">
                  <span><b>{achetees}</b> licences achetées</span>
                  <span><b>{attribuees}</b> attribuées</span>
                  <span><b>{o365}</b> utilisateurs avec accès Office 365 dans l'onglet Utilisateurs</span>
                  {o365 > achetees && <span className="sys-over">Plus d'utilisateurs que de licences</span>}
                </div>
              );
            }
            return <CarteTable key={sec.cle} sec={sec} startEdit={fresh === sec.cle} onDelete={del} intro={intro} notifier={notifier} enregistrer={(corps, message) => enregistrer(sec, corps, message)} />;
          }
          if (sec.type === "apps")
            return (
              <CarteTitre key={sec.cle} sec={sec} renommer={renommer} onDelete={del} extra={<span className="sys-hint">Comptes calculés à partir de l'onglet Utilisateurs</span>}>
                <TableApplications apps={apps} setApps={setApps} lignes={lignes} sites={sites} majApplication={majApplication} notifier={notifier} />
              </CarteTitre>
            );
          return null;
        })}
        {secs.length === 0 && sauvegardes && (
          <>
            <SiSauvegardesStats initial={releve} />
            <div className="sys-card sys-empty">Le tableau des sauvegardes a été supprimé.</div>
          </>
        )}
        {secs.length === 0 && !sauvegardes && <div className="sys-card sys-empty">Aucune section. Ajoutez une fiche ou un tableau depuis le menu.</div>}
      </div>
    </div>
  );
}
